import type { ObligationEntryDto, ObligationRegistryDto } from '@bk/contracts';
import { useState } from 'react';
import { useTenantQuery } from '../../../lib/useTenant';

/** Da dove viene un obbligo, in parole. */
const DA: Record<ObligationEntryDto['da'][number], string> = {
  impresa: 'l’impresa',
  impianti: 'un impianto',
  sedi: 'una sede',
  attivita: 'un’attività',
  software: 'i programmi',
  organizzazione: 'l’organizzazione',
  procedure: 'le procedure',
};
const FASE: Record<ObligationEntryDto['realizzato_da'][number]['fase'], string> = { impianti: 'impianto', programmi: 'programma', organizzazione: 'ruolo', procedure: 'procedura' };

/**
 * Il registro unico degli obblighi (contratto n. 1, fase 4; ADR 0157), in «La tua azienda»: tutti gli obblighi di legge
 * dell'azienda una volta sola, per area, con la norma, il fatto che li fa scattare, ogni quanto, chi ne risponde e quale
 * voce li realizza. Quelli che nessuna voce realizza ancora stanno in cima.
 */
export function Obblighi() {
  const q = useTenantQuery<ObligationRegistryDto>(['onboarding', 'obblighi'], '/onboarding/obblighi');
  const [aperta, setAperta] = useState<string | null>(null);
  const d = q.data;
  if (!d || !d.obblighi.length) return null;
  const aree = d.per_area.map((a) => a.area);
  // Gli obblighi che nessuna voce realizza ancora stanno in cima.
  const ordina = (xs: ObligationEntryDto[]) => [...xs].sort((a, b) => Number(!b.realizzato_da.length) - Number(!a.realizzato_da.length));
  return (
    <section className="space-y-3 rounded-3xl bg-white/70 p-5 ring-1 ring-black/5 md:p-7">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">Obblighi di legge</h2>
        <p className="text-sm text-ink/60">
          {d.obblighi.length} obblighi · {d.con_responsabile} con chi ne risponde
          {d.scoperti ? <span className="text-amber-700"> · {d.scoperti} senza una procedura, un impianto o un ruolo che li realizza</span> : null}
        </p>
      </header>
      <p className="text-xs text-ink/50">Raccolti una volta sola da tutta la configurazione, con la norma. Timonio li indica; decide l’azienda con i suoi consulenti.</p>
      {aree.map((area) => {
        const xs = ordina(d.obblighi.filter((o) => (o.area ?? 'Altro') === area));
        return (
          <details key={area} className="group rounded-2xl ring-1 ring-black/5" open={aree.length <= 2}>
            <summary className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium">
              <span>{area}</span>
              <span className="text-ink/50">
                {xs.length}
                {xs.some((o) => !o.realizzato_da.length) ? ` · ${xs.filter((o) => !o.realizzato_da.length).length} scoperti` : ''}
              </span>
            </summary>
            <ul className="divide-y divide-black/5">
              {xs.map((o) => (
                <li key={o.chiave} className="px-4 py-2 text-sm">
                  <button className="w-full text-left" onClick={() => setAperta(aperta === o.chiave ? null : o.chiave)}>
                    <span className={o.realizzato_da.length ? '' : 'text-amber-800'}>{o.cosa}</span>
                    <span className="ml-2 text-xs text-ink/50">{o.norma}</span>
                  </button>
                  {aperta === o.chiave && (
                    <dl className="mt-2 grid gap-1 text-xs text-ink/70 sm:grid-cols-[9rem_1fr]">
                      {o.perche && (
                        <>
                          <dt className="text-ink/50">Perché vale per te</dt>
                          <dd>{o.perche}</dd>
                        </>
                      )}
                      <dt className="text-ink/50">Ogni quanto</dt>
                      <dd>{o.cadenza ?? 'da stabilire'}</dd>
                      <dt className="text-ink/50">Chi ne risponde</dt>
                      <dd>{o.chi ?? 'da assegnare'}</dd>
                      {o.prova && (
                        <>
                          <dt className="text-ink/50">Cosa resta</dt>
                          <dd>{o.prova}</dd>
                        </>
                      )}
                      <dt className="text-ink/50">Chi lo realizza</dt>
                      <dd>{o.realizzato_da.length ? o.realizzato_da.map((r) => `${FASE[r.fase]} «${r.voce}»`).join(', ') : 'ancora nessuna voce: va nella diagnosi'}</dd>
                      <dt className="text-ink/50">Da dove viene</dt>
                      <dd>
                        {o.da.map((x) => DA[x]).join(', ')}
                        {o.riguarda.length ? ` · riguarda ${o.riguarda.join(', ')}` : ''}
                        {o.certezza === 'da_confermare' ? ' · da confermare' : ''}
                      </dd>
                    </dl>
                  )}
                </li>
              ))}
            </ul>
          </details>
        );
      })}
    </section>
  );
}
